import { useObserver } from 'mobx-react-lite'
import React, { FC, useMemo, useState, useEffect, useContext, useCallback } from 'react'
import { config } from '@config'
import formatCurrencyValueByLocale from '@utils/format-currency-value-by-locale'
import formatNumberByLocale from '@utils/format-number-by-locale'
import filter from 'lodash/filter'
import map from 'lodash/map'
import union from 'lodash/union'
import intersection from 'lodash/intersection'
import Divider from '@material-ui/core/Divider'
import { ProductOrderTable } from './ProductOrderTable'
import { ProductInfo } from './ProductInfo'
import Typography from '@components/std-components/Typography'
import * as css from './styles.css'
import { useTranslation } from 'react-i18next'
import { PrimaryButton } from '@components/UI-Components/PrimaryButton'
import { nav } from '@stores/nav'
import { CancelProductContext } from '../context/cancelProduct-context'
import { ConfirmationDialog } from '@components/UI-Components/ConfirmationDialog'
import NotificationConfirmation from './NotificationConfirmation'
import { useBuyingSessionGroup } from '@modules/wholesale/buyingGroupSummary/graphql/hooks'
import { isSelectedChannelBSGClosed } from '@services/wholesale/wholesaleBuyingSessionGroupsServices'
import { VIPChannel } from '@modules/common/models/enums/VIPChannel'
import { DefaultConfig } from '@constants'
export interface CancelProductComponentProps {
  product: any
  productOrderDetails: any
  selectedRows: any
  processedOrderData: any
  disabledOrders: any
  hasRetailChannel: boolean
  hasWholesaleChannel: boolean
  handleCancel: any
}

export const CancelProductComponent: FC<CancelProductComponentProps> = ({
  product,
  productOrderDetails,
  selectedRows,
  processedOrderData,
  disabledOrders,
  hasRetailChannel,
  hasWholesaleChannel,
  handleCancel
}) =>
  useObserver(function useHook() {
    const { t } = useTranslation()
    const [showConfirmationPopup, setShowConfirmationPopup] = useState(false)
    const [sendNotification, setSendNotification] = useState(true)
    const [selectedData, setSelectedData] = useState(selectedRows)
    const [filterList, setFilterList] = useState(selectedRows)
    const [isFilter, setFilter] = useState(false)

    const {
      queryParams: { buyingSessionGroupId, bspId }
    } = nav

    const {
      uiStore: { markCancelReplaceProduct, selectedChannel }
    } = useContext(CancelProductContext)

    useEffect(() => {
      setSelectedData(selectedRows)
      // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [selectedRows])

    const setSelectedOrder = selectedRows => {
      setSelectedData(selectedRows)
    }

    const handleRadioChange = option => {
      setSendNotification(option)
    }

    const { data: buyingSessionGroupData } = useBuyingSessionGroup(buyingSessionGroupId)

    const isChannelBSGClosed = isSelectedChannelBSGClosed(
      buyingSessionGroupData?.buyingSessionGroup,
      selectedChannel
    )
    const channelWiseSelectedData =
      selectedChannel === VIPChannel.Retail
        ? filter(selectedData || [], { internalClient: true })
        : filter(selectedData || [], { internalClient: false })

    const handleConfirm = () => {
      const clientOrderIds = []
      const doorOrderIds = []
      const cancelledList = filterList.length ? filterList : selectedData
      cancelledList.forEach(order => {
        if (order.isClientMaster) {
          clientOrderIds.push(order.id)
        } else {
          doorOrderIds.push(order.id)
        }
      })
      markCancelReplaceProduct(
        buyingSessionGroupId,
        bspId,
        clientOrderIds,
        doorOrderIds,
        sendNotification
      )
      setShowConfirmationPopup(false)
      handleCancel(true)
    }

    const handleFilterData = useCallback(
      (rows, isFilter) => {
        setFilter(isFilter)
        if (JSON.stringify(filterList) === JSON.stringify(rows)) return
        setFilterList(rows)
      },
      [filterList]
    )

    const getChannelWiseOrderTotals = (orders = []) => {
      return orders.reduce(
        (acc, curr: any) => {
          acc.value += curr?.globalValue || 0
          acc.quantity += curr?.orderQuantity || 0
          acc.count += 1
          acc.clients = union(acc.clients, curr?.Client?.id ? [curr?.Client?.id] : [])
          acc.doors = union(acc.doors, curr?.doorOrderIds || [])
          return acc
        },
        {
          value: 0,
          quantity: 0,
          count: 0,
          clients: [],
          doors: []
        }
      )
    }
    const productOrderTotals = useMemo(
      () => {
        const currency = config.globalCurrency || DefaultConfig.GLOBAL_CURRENCY
        const totals = productOrderDetails?.totals || {}
        //Selected orders
        const selectedOrderIds = map(
          isFilter ? filterList : selectedData,
          order => order.orderNumber
        )
        const filteredOrders = filter(productOrderDetails?.orders || [], order => {
          return !!intersection([order.orderId], selectedOrderIds).length
        })
        const retailOrders = filter(filteredOrders || [], { internalClient: true })
        const wholesaleOrders = filter(filteredOrders || [], { internalClient: false })

        //Retail Orders calculations
        const retailTotal = getChannelWiseOrderTotals(retailOrders)
        //Wholesale Orders calculations
        const wholesaleTotal = getChannelWiseOrderTotals(wholesaleOrders)
        //Selection Totals calculations
        const totalValue = retailTotal.value + wholesaleTotal.value
        const totalQuantity = retailTotal.quantity + wholesaleTotal.quantity
        const totalOrders = retailTotal.count + wholesaleTotal.count
        const cancelledTotals = {
          totalValue,
          totalQuantity,
          totalOrders,
          retailClients: retailTotal?.clients?.length,
          retailDoors: retailTotal?.doors?.length,
          retailOrders: retailTotal?.count,
          retailValue: retailTotal?.value,
          wholesaleClients: wholesaleTotal?.clients?.length,
          wholesaleDoors: wholesaleTotal?.doors?.length,
          wholesaleOrders: wholesaleTotal?.count,
          wholesaleValue: wholesaleTotal?.value
        }

        const productOrderSummary = {
          OrderSummary: [
            {
              selectionTotals: t('bb.cancelProduct.totals.totalValue', 'Total Value'),
              current: formatCurrencyValueByLocale(currency, +totals?.totalValue || 0, 2),
              cancelled: formatCurrencyValueByLocale(
                currency,
                +cancelledTotals?.totalValue || 0,
                2
              )
            },
            {
              selectionTotals: t(
                'bb.cancelProduct.totals.totalQuantity',
                'Total Quantity'
              ),
              current: formatNumberByLocale(totals?.totalQuantity || 0),
              cancelled: formatNumberByLocale(cancelledTotals?.totalQuantity || 0)
            },
            {
              selectionTotals: t(
                'bb.cancelProduct.totals.numberOfOrders',
                'Number Of Orders'
              ),
              current: formatNumberByLocale(totals?.totalOrders || 0),
              cancelled: formatNumberByLocale(cancelledTotals?.totalOrders || 0)
            },
            {
              selectionTotals: t(
                'bb.cancelProduct.totals.ordersSentToSap',
                'Orders Sent To Sap'
              ),
              current: formatNumberByLocale(totals?.sentToSAPOrders || 0),
              cancelled: null // GBB-8693: don't display SAP details in cancelled column
            },
            {
              selectionTotals: t(
                'bb.cancelProduct.totals.valueSentToSap',
                'Value Sent To Sap'
              ),
              current: formatCurrencyValueByLocale(
                currency,
                +totals?.sentToSAPValue || 0,
                2
              ),
              cancelled: null // GBB-8693: don't display SAP details in cancelled column
            },
            {
              selectionTotals: t(
                'bb.cancelProduct.totals.unitsSentToSap',
                'Units Sent To Sap'
              ),
              current: formatNumberByLocale(totals?.sentToSAPQuantity || 0),
              cancelled: null // GBB-8693: don't display SAP details in cancelled column
            }
          ],
          RetailTotals: [
            {
              retail: t('bb.cancelProduct.retailTotals.zones', 'Zones'),
              current: formatNumberByLocale(totals?.retailClients || 0),
              cancelled: formatNumberByLocale(cancelledTotals?.retailClients || 0)
            },
            {
              retail: t('bb.cancelProduct.retailTotals.stores', 'Stores'),
              current: formatNumberByLocale(totals?.retailDoors || 0),
              cancelled: formatNumberByLocale(cancelledTotals?.retailDoors || 0)
            },
            {
              retail: t('bb.cancelProduct.retailTotals.orders', 'Orders'),
              current: formatNumberByLocale(totals?.retailOrders || 0),
              cancelled: formatNumberByLocale(cancelledTotals?.retailOrders || 0)
            },
            {
              retail: t('bb.cancelProduct.retailTotals.value', 'Value'),
              current: formatCurrencyValueByLocale(
                currency,
                +totals?.retailValue || 0,
                2
              ),
              cancelled: formatCurrencyValueByLocale(
                currency,
                +cancelledTotals?.retailValue || 0,
                2
              )
            }
          ],
          WholesaleTotals: [
            {
              wholesale: t('bb.cancelProduct.wholesaleTotals.clients', 'Clients'),
              current: formatNumberByLocale(totals?.wholesaleClients || 0),
              cancelled: formatNumberByLocale(cancelledTotals?.wholesaleClients || 0)
            },
            {
              wholesale: t('bb.cancelProduct.wholesaleTotals.doors', 'Doors'),
              current: formatNumberByLocale(totals?.wholesaleDoors || 0),
              cancelled: formatNumberByLocale(cancelledTotals?.wholesaleDoors || 0)
            },
            {
              wholesale: t('bb.cancelProduct.wholesaleTotals.orders', 'Orders'),
              current: formatNumberByLocale(totals?.wholesaleOrders || 0),
              cancelled: formatNumberByLocale(cancelledTotals?.wholesaleOrders || 0)
            },
            {
              wholesale: t('bb.cancelProduct.wholesaleTotals.value', 'Value'),
              current: formatCurrencyValueByLocale(
                currency,
                +totals?.wholesaleValue || 0,
                2
              ),
              cancelled: formatCurrencyValueByLocale(
                currency,
                +cancelledTotals?.wholesaleValue || 0,
                2
              )
            }
          ]
        }
        return productOrderSummary
      },
      // eslint-disable-next-line react-hooks/exhaustive-deps
      [productOrderDetails, selectedData, filterList, isFilter]
    )

    return (
      <>
        {/* Product Order Summary */}
        <ProductInfo
          product={product}
          productOrderTotals={productOrderTotals}
          hasRetailChannel={hasRetailChannel}
          hasWholesaleChannel={hasWholesaleChannel}
        />
        <Divider className={css.divider} />
        {/* Order Table */}
        <ProductOrderTable
          setFilterData={handleFilterData}
          setSelectedData={setSelectedOrder}
          selectedRows={selectedRows}
          selectedData={selectedData}
          processedOrderData={processedOrderData}
          disabledOrders={disabledOrders}
          hasRetailChannel={hasRetailChannel}
          hasWholesaleChannel={hasWholesaleChannel}
          isSelectedChannelBSGClosed={isChannelBSGClosed}
        />
        <Divider className={css.divSpaceFooter} />
        {/* Popup Footer */}
        <div className={css.popupFooter}>
          <Typography
            variant={'buttonlabel'}
            classes={css.actionBtn}
            style={{ fontWeight: 'normal' }}
            onClick={() => handleCancel(false)}
          >
            {t('common.label.cancel', 'Cancel').toUpperCase()}
          </Typography>
          <PrimaryButton
            onClick={() => setShowConfirmationPopup(true)}
            disabled={
              (isFilter ? !filterList?.length : !selectedData?.length) ||
              isChannelBSGClosed ||
              !channelWiseSelectedData.length
            }
          >
            {t('common.label.confirm', 'Confirm').toUpperCase()}
          </PrimaryButton>
        </div>
        {showConfirmationPopup && (
          <ConfirmationDialog
            open={showConfirmationPopup}
            title={t(
              'productDetails.cancelProductPopup.confirmationTitle',
              'CONFIRM CANCELATION'
            )}
            messageEle={NotificationConfirmation(
              {
                name: product?.nodeInfo?.title || '',
                sku: product?.nodeInfo?.subtitle || ''
              },
              sendNotification,
              handleRadioChange
            )}
            handleClose={() => handleCancel(false)}
            handleBack={() => setShowConfirmationPopup(false)}
            handleDelete={handleConfirm}
            showBackButton={true}
            proceedButtonTitle={t('common.label.confirm', 'Confirm').toUpperCase()}
            cancelButtonTitle={t('common.label.cancel', 'Cancel').toUpperCase()}
          />
        )}
      </>
    )
  })
